import { Project } from "projen/lib";
import { ProjectMetadata } from "../../projects/project-metadata";
import { SlackMetadata } from "../../projects/project-metadata-options";
import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE } from "../types";

/**
 * Returns true when the project's resolved metadata declares a Slack
 * integration. A declared integration means any of:
 *
 * - `slack.projectChannel` is set
 * - `slack.alertsChannel` is set
 * - `slack.channels` has at least one entry
 *
 * An empty `slack: {}` block is treated as "not configured" so consumers
 * do not accidentally activate the bundle by reserving the key.
 */
function hasSlackConfig(slack: SlackMetadata | undefined): boolean {
  if (!slack) {
    return false;
  }
  if (slack.projectChannel || slack.alertsChannel) {
    return true;
  }
  if (slack.channels && Object.keys(slack.channels).length > 0) {
    return true;
  }
  return false;
}

/**
 * Slack bundle — auto-activated when the consuming project declares a
 * Slack integration via `ProjectMetadata.slack`. Consumers can still
 * opt in manually via `includeBundles: ["slack"]` when they have no
 * `ProjectMetadata` configured.
 *
 * Detection signal: any truthy value on
 * `ProjectMetadata.of(project).metadata.slack` that carries at least one
 * channel reference (`projectChannel`, `alertsChannel`, or a non-empty
 * `channels` record).
 */
export const slackBundle: AgentRuleBundle = {
  name: "slack",
  description:
    "Slack MCP message formatting and best practices. Auto-activates when ProjectMetadata declares a Slack integration; can still be force-included via `includeBundles: ['slack']`.",
  appliesWhen: (project: Project) => {
    const pm = ProjectMetadata.of(project);
    return hasSlackConfig(pm?.metadata.slack);
  },
  rules: [
    {
      name: "slack-message-formatting",
      description:
        "Format Slack messages with explicit links so bullets and labels render correctly",
      scope: AGENT_RULE_SCOPE.ALWAYS,
      content: [
        "# Slack Message Formatting",
        "",
        "When composing or sending messages to Slack (e.g., via Slack MCP tools like `slack_send_message`), use formatting that Slack's mrkdwn parser renders correctly.",
        "",
        "## Rules",
        "",
        "1. **Use Slack's explicit link syntax** for any link in a message:",
        "   - Format: `<https://example.com|display text>`",
        "   - Text outside the angle brackets (bullets, labels) stays separate and won't merge into the link",
        "",
        "2. **Do not rely on auto-linkification** when the message has bullets or labels before URLs — auto-linked URLs can break or merge with surrounding text",
        "",
        "3. **Put each list item on its own line** with a newline between items so list structure is clear",
        "",
        "4. **Example — preferred:**",
        "   ```",
        "   Status update:",
        "",
        "   • Feature A: <https://github.com/org/repo/pull/1|repo#1>",
        "   • Feature B: <https://github.com/org/repo/pull/2|repo#2>",
        "   ```",
        "",
        "5. **Avoid:** Plain URLs immediately after a bullet/label on the same line (e.g., `• Feature A: https://github.com/...`) when sending via API, as it can render with bullets inside or between links",
        "",
        "## Activation",
        "",
        "This bundle auto-activates when the consuming project declares a Slack integration through `ProjectMetadata` (any of `slack.projectChannel`, `slack.alertsChannel`, or a non-empty `slack.channels` record). If no `ProjectMetadata` is configured, opt in manually via `AgentConfigOptions.includeBundles: ['slack']`.",
      ].join("\n"),
      tags: ["workflow"],
    },
  ],
};
